// ============================================
// Hemologix Seed Data: Protocol Bundles (1-25)
// ============================================

import type { Bundle } from '../../types/formulary';

export const SEED_BUNDLES: Bundle[] = [
  // ============================================================
  // TIER 1 — OTC Supplement Bundles (1-10)
  // ============================================================

  // --- 1. Essential Foundation ---
  {
    id: 'bundle-essential-foundation',
    bundleNumber: 1,
    name: 'Essential Foundation',
    tier: 1,
    target: 'Every patient baseline',
    description:
      'Comprehensive foundational supplement stack addressing the most common nutritional deficiencies. Designed as the universal starting point for every patient before layering specialized protocols.',
    products: [
      { productId: 'multivitamin', productName: 'High-Potency Multivitamin', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-d3-k2', productName: 'Vitamin D3 + K2', isOptional: false, sortOrder: 2 },
      { productId: 'omega-3', productName: 'Omega-3 Fish Oil (EPA/DHA)', isOptional: false, sortOrder: 3 },
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 4 },
      { productId: 'probiotic', productName: 'Multi-Strain Probiotic', isOptional: false, sortOrder: 5 },
      { productId: 'b-complex', productName: 'Methylated B-Complex', isOptional: false, sortOrder: 6 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Vitamin D (25-OH)', operator: 'lt', value: 50 },
        { biomarker: 'Magnesium (RBC)', operator: 'lt', value: 5.5 },
        { biomarker: 'B12', operator: 'lt', value: 500 },
      ],
      goalConditions: ['energy'],
      minimumMatchCount: 1,
      description: 'Universal baseline — any suboptimal nutritional marker or new patient intake',
    },
    pillsPerDay: '7-8',
    priceRange: '$149/mo',
    upsellPath: 'Add Metabolic Reset or Inflammation Control based on labs',
    upsellBundleId: 'bundle-metabolic-reset',
    contraindications: [],
    warnings: ['Adjust iron intake if ferritin >150 (male) or >100 (female)'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 1,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 2. Metabolic Reset ---
  {
    id: 'bundle-metabolic-reset',
    bundleNumber: 2,
    name: 'Metabolic Reset',
    tier: 1,
    target: 'Fasting insulin >7, glucose >95, metabolic dysfunction',
    description:
      'Targeted supplement stack for early metabolic dysfunction — insulin resistance, elevated glucose, and metabolic syndrome markers before pharmacological intervention is warranted.',
    products: [
      { productId: 'multivitamin', productName: 'High-Potency Multivitamin', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-d3-k2', productName: 'Vitamin D3 + K2', isOptional: false, sortOrder: 2 },
      { productId: 'omega-3', productName: 'Omega-3 Fish Oil (EPA/DHA)', isOptional: false, sortOrder: 3 },
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 4 },
      { productId: 'berberine', productName: 'Berberine HCl', isOptional: false, sortOrder: 5 },
      { productId: 'green-tea-extract', productName: 'Green Tea Extract (EGCG)', isOptional: false, sortOrder: 6 },
      { productId: 'chromium', productName: 'Chromium Picolinate', isOptional: false, sortOrder: 7 },
      { productId: 'coq10', productName: 'CoQ10 (Ubiquinol)', isOptional: false, sortOrder: 8 },
      { productId: 'fiber-supplement', productName: 'Soluble Fiber (Psyllium)', isOptional: true, sortOrder: 9 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Fasting Insulin', operator: 'gt', value: 7 },
        { biomarker: 'Fasting Glucose', operator: 'gt', value: 95 },
        { biomarker: 'Hemoglobin A1c', operator: 'gt', value: 5.3 },
        { biomarker: 'HOMA-IR', operator: 'gt', value: 1.5 },
      ],
      goalConditions: ['body_composition'],
      minimumMatchCount: 1,
      description: 'Fasting insulin >7, glucose >95, A1C >5.3, or HOMA-IR >1.5',
    },
    pillsPerDay: '9-11',
    priceRange: '$149/mo',
    upsellPath: 'Escalate to Metabolic Rx (Tier 2) if A1C >5.5 or insulin >10 after 90 days',
    upsellBundleId: 'bundle-metabolic-rx',
    contraindications: ['Berberine may interact with Metformin — reduce berberine if adding Rx'],
    warnings: ['Berberine can lower blood sugar — monitor if diabetic', 'Green tea extract on empty stomach may cause nausea'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 2,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 3. Inflammation Control ---
  {
    id: 'bundle-inflammation-control',
    bundleNumber: 3,
    name: 'Inflammation Control',
    tier: 1,
    target: 'hs-CRP >1.0, elevated inflammatory markers',
    description:
      'Anti-inflammatory supplement stack targeting systemic chronic inflammation. Combines curcumin, NAC, and alpha-lipoic acid with the foundation stack for comprehensive inflammation management.',
    products: [
      { productId: 'multivitamin', productName: 'High-Potency Multivitamin', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-d3-k2', productName: 'Vitamin D3 + K2', isOptional: false, sortOrder: 2 },
      { productId: 'omega-3', productName: 'Omega-3 Fish Oil (EPA/DHA)', isOptional: false, sortOrder: 3 },
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 4 },
      { productId: 'curcumin', productName: 'Curcumin (with Piperine)', isOptional: false, sortOrder: 5 },
      { productId: 'nac', productName: 'N-Acetyl Cysteine (NAC)', isOptional: false, sortOrder: 6 },
      { productId: 'alpha-lipoic-acid', productName: 'Alpha-Lipoic Acid (ALA)', isOptional: false, sortOrder: 7 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'hs-CRP', operator: 'gt', value: 1.0 },
        { biomarker: 'Homocysteine', operator: 'gt', value: 9 },
        { biomarker: 'ESR', operator: 'gt', value: 15 },
      ],
      goalConditions: ['immune'],
      minimumMatchCount: 1,
      description: 'hs-CRP >1.0, Homocysteine >9, or ESR >15',
    },
    pillsPerDay: '8-10',
    priceRange: '$149/mo',
    upsellPath: 'Add LDN (Tier 2) for refractory inflammation or autoimmune component',
    contraindications: ['NAC may interact with nitroglycerin — avoid concurrent use'],
    warnings: ['Curcumin may increase bleeding risk with anticoagulants', 'ALA may lower blood sugar — monitor if diabetic'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 3,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 4. Thyroid Support ---
  {
    id: 'bundle-thyroid-support',
    bundleNumber: 4,
    name: 'Thyroid Support',
    tier: 1,
    target: 'TSH >2.5, low Free T3, subclinical thyroid dysfunction',
    description:
      'Nutritional support for optimal thyroid function. Selenium and zinc are essential cofactors for T4-to-T3 conversion and thyroid peroxidase activity.',
    products: [
      { productId: 'multivitamin', productName: 'High-Potency Multivitamin', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-d3-k2', productName: 'Vitamin D3 + K2', isOptional: false, sortOrder: 2 },
      { productId: 'omega-3', productName: 'Omega-3 Fish Oil (EPA/DHA)', isOptional: false, sortOrder: 3 },
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 4 },
      { productId: 'selenium', productName: 'Selenium (Selenomethionine)', isOptional: false, sortOrder: 5 },
      { productId: 'zinc', productName: 'Zinc Picolinate', isOptional: false, sortOrder: 6 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'TSH', operator: 'gt', value: 2.5 },
        { biomarker: 'Free T3', operator: 'lt', value: 3.0 },
        { biomarker: 'Free T4', operator: 'lt', value: 1.2 },
      ],
      goalConditions: ['energy', 'body_composition'],
      minimumMatchCount: 1,
      description: 'TSH >2.5, Free T3 <3.0, or Free T4 <1.2',
    },
    pillsPerDay: '7-8',
    priceRange: '$129/mo',
    upsellPath: 'Escalate to Thyroid Rx (Tier 2) if TSH >4.0 or symptoms persist after 90 days',
    upsellBundleId: 'bundle-thyroid-rx',
    contraindications: ['Excess selenium (>400mcg/day) causes toxicity — do not stack with other selenium sources'],
    warnings: ['Take zinc and selenium at separate times from thyroid medications (4-hour gap)'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 4,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 5. Iron Recovery (Female) ---
  {
    id: 'bundle-iron-recovery',
    bundleNumber: 5,
    name: 'Iron Recovery (Female)',
    tier: 1,
    target: 'Ferritin <30, iron deficiency, menstruating women',
    description:
      'Targeted iron repletion for women with low ferritin. Vitamin C enhances non-heme iron absorption. Foundation stack included for comprehensive nutritional support.',
    products: [
      { productId: 'multivitamin', productName: 'High-Potency Multivitamin', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-d3-k2', productName: 'Vitamin D3 + K2', isOptional: false, sortOrder: 2 },
      { productId: 'omega-3', productName: 'Omega-3 Fish Oil (EPA/DHA)', isOptional: false, sortOrder: 3 },
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 4 },
      { productId: 'iron-bisglycinate', productName: 'Iron Bisglycinate', isOptional: false, sortOrder: 5 },
      { productId: 'vitamin-c', productName: 'Vitamin C', isOptional: false, sortOrder: 6 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Ferritin', operator: 'lt', value: 30 },
        { biomarker: 'Iron (serum)', operator: 'lt', value: 60 },
        { biomarker: 'Hemoglobin', operator: 'lt', value: 12.5 },
      ],
      minimumMatchCount: 1,
      description: 'Ferritin <30, serum iron <60, or hemoglobin <12.5',
    },
    pillsPerDay: '7-8',
    priceRange: '$129/mo',
    contraindications: [
      'Hemochromatosis (iron overload disorder) — CONTRAINDICATED',
      'Do not use if ferritin >100 without clinical justification',
    ],
    warnings: [
      'Separate iron from thyroid meds, calcium, and coffee by 2+ hours',
      'Take iron with vitamin C on empty stomach for best absorption',
      'Recheck ferritin at 8 weeks',
    ],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'female',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 5,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 6. Women's Hormone Balance ---
  {
    id: 'bundle-womens-hormone-balance',
    bundleNumber: 6,
    name: "Women's Hormone Balance",
    tier: 1,
    target: 'Estrogen dominance symptoms, PMS, irregular cycles',
    description:
      'OTC hormone-balancing stack for premenopausal women with estrogen dominance symptoms. DIM and calcium D-glucarate support estrogen metabolism and detoxification pathways.',
    products: [
      { productId: 'multivitamin', productName: 'High-Potency Multivitamin', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-d3-k2', productName: 'Vitamin D3 + K2', isOptional: false, sortOrder: 2 },
      { productId: 'omega-3', productName: 'Omega-3 Fish Oil (EPA/DHA)', isOptional: false, sortOrder: 3 },
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 4 },
      { productId: 'dim', productName: 'DIM (Diindolylmethane)', isOptional: false, sortOrder: 5 },
      { productId: 'calcium-d-glucarate', productName: 'Calcium D-Glucarate', isOptional: false, sortOrder: 6 },
      { productId: 'evening-primrose', productName: 'Evening Primrose Oil', isOptional: true, sortOrder: 7 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Estradiol (day 3)', operator: 'gt', value: 100 },
        { biomarker: 'Progesterone (day 21)', operator: 'lt', value: 10 },
      ],
      goalConditions: ['hormonal'],
      minimumMatchCount: 1,
      description: 'Estradiol elevated, progesterone low, estrogen dominance symptoms',
    },
    pillsPerDay: '8-10',
    priceRange: '$149/mo',
    upsellPath: 'Escalate to Female HRT (Tier 2) if perimenopausal or postmenopausal',
    upsellBundleId: 'bundle-female-hrt',
    contraindications: ['Breast cancer history — DIM use controversial, consult oncologist'],
    warnings: ['DIM may alter estrogen metabolite ratios — recheck in 8 weeks'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'female',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 6,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 7. Men's Optimization ---
  {
    id: 'bundle-mens-optimization',
    bundleNumber: 7,
    name: "Men's Optimization",
    tier: 1,
    target: 'Total T <500, low energy, low libido, early andropause',
    description:
      'Natural testosterone support stack for men with suboptimal levels. Tongkat ali, boron, zinc, and ashwagandha support endogenous testosterone production before considering HRT.',
    products: [
      { productId: 'multivitamin', productName: 'High-Potency Multivitamin', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-d3-k2', productName: 'Vitamin D3 + K2', isOptional: false, sortOrder: 2 },
      { productId: 'omega-3', productName: 'Omega-3 Fish Oil (EPA/DHA)', isOptional: false, sortOrder: 3 },
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 4 },
      { productId: 'tongkat-ali', productName: 'Tongkat Ali (Eurycoma longifolia)', isOptional: false, sortOrder: 5 },
      { productId: 'boron', productName: 'Boron', isOptional: false, sortOrder: 6 },
      { productId: 'zinc', productName: 'Zinc Picolinate', isOptional: false, sortOrder: 7 },
      { productId: 'ashwagandha', productName: 'Ashwagandha (KSM-66)', isOptional: false, sortOrder: 8 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Total Testosterone', operator: 'lt', value: 500 },
        { biomarker: 'Free Testosterone', operator: 'lt', value: 15 },
        { biomarker: 'SHBG', operator: 'gt', value: 40 },
      ],
      goalConditions: ['hormonal', 'energy', 'sexual_health'],
      minimumMatchCount: 1,
      description: 'Total T <500, Free T <15, or SHBG >40',
    },
    pillsPerDay: '9-10',
    priceRange: '$149/mo',
    upsellPath: 'Escalate to Male HRT (Tier 2) if Total T <350 or no improvement after 90 days',
    upsellBundleId: 'bundle-male-hrt',
    contraindications: ['Prostate cancer — avoid testosterone-boosting supplements'],
    warnings: ['Recheck Total T, Free T, and SHBG at 8-12 weeks'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'male',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 7,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 8. Prostate Health ---
  {
    id: 'bundle-prostate-health',
    bundleNumber: 8,
    name: 'Prostate Health',
    tier: 1,
    target: 'PSA concerns, BPH symptoms, prostate health optimization',
    description:
      'Prostate-focused supplement stack combining saw palmetto with zinc and boron for 5-alpha reductase inhibition and prostate cell health.',
    products: [
      { productId: 'multivitamin', productName: 'High-Potency Multivitamin', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-d3-k2', productName: 'Vitamin D3 + K2', isOptional: false, sortOrder: 2 },
      { productId: 'omega-3', productName: 'Omega-3 Fish Oil (EPA/DHA)', isOptional: false, sortOrder: 3 },
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 4 },
      { productId: 'saw-palmetto', productName: 'Saw Palmetto Extract', isOptional: false, sortOrder: 5 },
      { productId: 'zinc', productName: 'Zinc Picolinate', isOptional: false, sortOrder: 6 },
      { productId: 'boron', productName: 'Boron', isOptional: false, sortOrder: 7 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'PSA', operator: 'gt', value: 1.5 },
      ],
      goalConditions: ['prostate'],
      minimumMatchCount: 1,
      description: 'PSA >1.5 or BPH symptoms reported',
    },
    pillsPerDay: '8-9',
    priceRange: '$139/mo',
    contraindications: ['Active prostate cancer — consult urologist before supplementation'],
    warnings: ['Saw palmetto may affect PSA readings — inform urologist', 'Monitor PSA q6 months'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'male',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 8,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 9. Sleep + Recovery ---
  {
    id: 'bundle-sleep-recovery',
    bundleNumber: 9,
    name: 'Sleep + Recovery',
    tier: 1,
    target: 'Poor sleep quality, recovery optimization',
    description:
      'Evidence-based sleep and recovery stack. Magnesium glycinate promotes GABA-mediated relaxation, melatonin regulates circadian rhythm, and ashwagandha reduces cortisol.',
    products: [
      { productId: 'magnesium-glycinate', productName: 'Magnesium Glycinate', isOptional: false, sortOrder: 1 },
      { productId: 'melatonin', productName: 'Melatonin (low-dose)', isOptional: false, sortOrder: 2 },
      { productId: 'ashwagandha', productName: 'Ashwagandha (KSM-66)', isOptional: false, sortOrder: 3 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Cortisol (AM)', operator: 'gt', value: 18 },
      ],
      goalConditions: ['sleep'],
      minimumMatchCount: 1,
      description: 'Elevated evening cortisol, poor sleep quality reported, or sleep optimization goal',
    },
    pillsPerDay: '3-4',
    priceRange: '$129/mo',
    upsellPath: 'Add Foundation stack for comprehensive coverage',
    upsellBundleId: 'bundle-essential-foundation',
    contraindications: [],
    warnings: ['Melatonin: start at 0.3-0.5mg, max 3mg — more is not better', 'Take all 30-60 min before bed'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 9,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 10. Beauty + Skin ---
  {
    id: 'bundle-beauty-skin',
    bundleNumber: 10,
    name: 'Beauty + Skin',
    tier: 1,
    target: 'Skin, hair, and nail health optimization',
    description:
      'Collagen-centric beauty stack with antioxidant support. Marine collagen peptides, vitamin C (cofactor for collagen synthesis), vitamin E, and probiotics for gut-skin axis health.',
    products: [
      { productId: 'collagen-peptides', productName: 'Marine Collagen Peptides', isOptional: false, sortOrder: 1 },
      { productId: 'vitamin-c', productName: 'Vitamin C', isOptional: false, sortOrder: 2 },
      { productId: 'vitamin-e', productName: 'Vitamin E (Mixed Tocopherols)', isOptional: false, sortOrder: 3 },
      { productId: 'probiotic', productName: 'Multi-Strain Probiotic', isOptional: false, sortOrder: 4 },
    ],
    trigger: {
      conditions: [],
      goalConditions: ['body_composition'],
      minimumMatchCount: 1,
      description: 'Skin, hair, or nail health goals',
    },
    pillsPerDay: '4-5',
    priceRange: '$139/mo',
    upsellPath: 'Add GHK-Cu (Tier 3) for advanced skin regeneration',
    upsellBundleId: 'bundle-nad-cellular',
    contraindications: [],
    warnings: ['Marine collagen: avoid if shellfish allergy'],
    requiresProvider: false,
    checkoutNote: 'Ships direct. No provider visit required.',
    gender: 'any',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 10,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // ============================================================
  // TIER 2 — Prescription Bundles (11-21)
  // ============================================================

  // --- 11. Metabolic Rx ---
  {
    id: 'bundle-metabolic-rx',
    bundleNumber: 11,
    name: 'Metabolic Rx',
    tier: 2,
    target: 'A1C >5.5, fasting insulin >10, metabolic syndrome',
    description:
      'Pharmaceutical metabolic intervention combining metformin ER with berberine for dual-pathway insulin sensitization. Metformin activates AMPK; berberine acts on similar but distinct metabolic pathways.',
    products: [
      { productId: 'metformin-er', productName: 'Metformin ER', dosageOverride: '500-1000mg daily with dinner', isOptional: false, sortOrder: 1 },
      { productId: 'berberine', productName: 'Berberine HCl', dosageOverride: '500mg 2x/day (reduce to 500mg 1x/day if combining with metformin)', isOptional: false, sortOrder: 2 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Hemoglobin A1c', operator: 'gt', value: 5.5 },
        { biomarker: 'Fasting Insulin', operator: 'gt', value: 10 },
        { biomarker: 'HOMA-IR', operator: 'gt', value: 2.0 },
      ],
      minimumMatchCount: 1,
      description: 'A1C >5.5, fasting insulin >10, or HOMA-IR >2.0',
    },
    pillsPerDay: '2-3',
    priceRange: '$199/mo',
    upsellPath: 'Add GLP-1 (Weight Loss Rx) if BMI >30 and inadequate response',
    upsellBundleId: 'bundle-weight-loss-rx',
    contraindications: [
      'eGFR <30 (metformin contraindicated)',
      'Liver disease (lactic acidosis risk with metformin)',
      'Active alcohol use disorder',
      'Planned IV contrast within 48 hours',
    ],
    warnings: [
      'GI side effects common at initiation — titrate metformin slowly',
      'Monitor B12 annually (metformin depletes B12)',
      'Reduce berberine dose when combining with metformin',
      'Hold metformin 48h before/after IV contrast procedures',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Provider sends Rx to pharmacy.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 11,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 12. Male HRT ---
  {
    id: 'bundle-male-hrt',
    bundleNumber: 12,
    name: 'Male HRT',
    tier: 2,
    target: 'Total T <350, symptomatic hypogonadism',
    description:
      'Comprehensive male hormone replacement therapy. Testosterone cypionate for androgen replacement, anastrozole for estrogen control, and HCG to maintain testicular function and fertility.',
    products: [
      { productId: 'testosterone-cypionate', productName: 'Testosterone Cypionate', dosageOverride: '100-200mg IM or SubQ weekly', isOptional: false, sortOrder: 1 },
      { productId: 'anastrozole', productName: 'Anastrozole', dosageOverride: '0.25-0.5mg 2x/week (PRN based on E2 levels)', isOptional: true, sortOrder: 2 },
      { productId: 'hcg', productName: 'HCG (Human Chorionic Gonadotropin)', dosageOverride: '500-1000 IU 2-3x/week', isOptional: true, sortOrder: 3 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Total Testosterone', operator: 'lt', value: 350 },
        { biomarker: 'Free Testosterone', operator: 'lt', value: 10 },
      ],
      goalConditions: ['hormonal', 'sexual_health', 'energy'],
      minimumMatchCount: 1,
      description: 'Total T <350 or Free T <10 with symptoms',
    },
    priceRange: '$249/mo',
    upsellPath: 'Add Longevity Core (Tier 3) for advanced optimization',
    upsellBundleId: 'bundle-longevity-core',
    contraindications: [
      'Prostate cancer (active or history)',
      'Breast cancer in males',
      'Hematocrit >54% (polycythemia risk)',
      'Untreated severe sleep apnea',
      'Planning conception (use HCG monotherapy instead)',
      'PSA >4.0 without urological evaluation',
    ],
    warnings: [
      'Monitor hematocrit q3-6 months — therapeutic phlebotomy if >54%',
      'Check PSA at baseline and q6-12 months',
      'Monitor estradiol — adjust anastrozole PRN',
      'HCG maintains fertility; omit if fertility not desired',
      'Cardiovascular risk assessment required before initiation',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Labs required before prescribing. Provider sends Rx program link.',
    gender: 'male',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 12,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 13. Female HRT ---
  {
    id: 'bundle-female-hrt',
    bundleNumber: 13,
    name: 'Female HRT',
    tier: 2,
    target: 'Postmenopausal, perimenopause, E2 <50',
    description:
      'Bioidentical hormone replacement for peri/postmenopausal women. Estradiol for vasomotor and genitourinary symptoms, micronized progesterone for endometrial protection, and low-dose testosterone cream for libido and energy.',
    products: [
      { productId: 'estradiol', productName: 'Estradiol (Patch or Cream)', dosageOverride: '0.025-0.1mg/day patch or 0.5-1mg topical', isOptional: false, sortOrder: 1 },
      { productId: 'progesterone', productName: 'Micronized Progesterone', dosageOverride: '100-200mg oral at bedtime (if uterus intact)', isOptional: false, sortOrder: 2 },
      { productId: 'testosterone-cream-female', productName: 'Testosterone Cream (Female)', dosageOverride: '0.5-1mg topical daily', isOptional: true, sortOrder: 3 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Estradiol (day 3)', operator: 'lt', value: 50 },
        { biomarker: 'Progesterone (day 21)', operator: 'lt', value: 1.8 },
      ],
      goalConditions: ['hormonal', 'sexual_health', 'longevity'],
      minimumMatchCount: 1,
      description: 'Estradiol <50 pg/mL (postmenopausal) or progesterone <1.8',
    },
    priceRange: '$249/mo',
    contraindications: [
      'Breast cancer (active or history) — estradiol generally contraindicated',
      'Active endometrial cancer',
      'Unexplained vaginal bleeding',
      'Active DVT or PE',
      'Active liver disease',
      'History of blood clots (relative — transdermal may be acceptable)',
    ],
    warnings: [
      'Transdermal estradiol preferred over oral (lower clotting risk)',
      'Progesterone required if uterus intact (endometrial protection)',
      'Monitor breast health — mammogram per guidelines',
      'Recheck hormone levels at 8-12 weeks',
      'Review cardiovascular risk factors',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Labs required before prescribing. Provider sends Rx program link.',
    gender: 'female',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 13,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 14. Thyroid Rx ---
  {
    id: 'bundle-thyroid-rx',
    bundleNumber: 14,
    name: 'Thyroid Rx',
    tier: 2,
    target: 'TSH >4.0, low Free T3/T4, clinical hypothyroidism',
    description:
      'Prescription thyroid replacement. Levothyroxine (T4) with optional liothyronine (T3) or desiccated thyroid (Armour/NP Thyroid) for patients with poor T4-to-T3 conversion.',
    products: [
      { productId: 'levothyroxine', productName: 'Levothyroxine (T4)', dosageOverride: '25-200mcg daily (titrate per TSH)', isOptional: false, sortOrder: 1 },
      { productId: 'liothyronine', productName: 'Liothyronine (T3) or Armour Thyroid', dosageOverride: '5-25mcg daily or Armour 30-120mg daily', isOptional: true, sortOrder: 2 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'TSH', operator: 'gt', value: 4.0 },
        { biomarker: 'Free T4', operator: 'lt', value: 0.9 },
        { biomarker: 'Free T3', operator: 'lt', value: 2.3 },
      ],
      minimumMatchCount: 1,
      description: 'TSH >4.0, Free T4 <0.9, or Free T3 <2.3',
    },
    priceRange: '$179/mo',
    contraindications: [
      'Untreated adrenal insufficiency (correct cortisol first)',
      'Acute MI (start at very low dose)',
      'Thyrotoxicosis',
    ],
    warnings: [
      'Take on empty stomach, 30-60 min before food',
      'Separate from iron, calcium, antacids by 4 hours',
      'Recheck TSH at 6-8 weeks after any dose change',
      'Titrate slowly in elderly or cardiac patients',
      'Monitor Free T3, Free T4, and Reverse T3 — not just TSH',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Labs required before prescribing.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 14,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 15. Weight Loss Rx ---
  {
    id: 'bundle-weight-loss-rx',
    bundleNumber: 15,
    name: 'Weight Loss Rx',
    tier: 2,
    target: 'BMI >30, or BMI >27 with metabolic comorbidity',
    description:
      'GLP-1 receptor agonist-based weight management. Semaglutide or tirzepatide for appetite regulation and metabolic improvement, combined with metformin for insulin sensitization.',
    products: [
      { productId: 'semaglutide', productName: 'Semaglutide (or Tirzepatide)', dosageOverride: 'Semaglutide: start 0.25mg/wk, titrate to 2.4mg/wk; Tirzepatide: start 2.5mg/wk, titrate to 15mg/wk', isOptional: false, sortOrder: 1 },
      { productId: 'metformin-er', productName: 'Metformin ER', dosageOverride: '500-1000mg daily', isOptional: true, sortOrder: 2 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Fasting Insulin', operator: 'gt', value: 12 },
        { biomarker: 'Hemoglobin A1c', operator: 'gt', value: 5.7 },
      ],
      goalConditions: ['body_composition'],
      minimumMatchCount: 1,
      description: 'BMI >30 (or >27 with comorbidities), insulin >12, A1C >5.7',
    },
    priceRange: '$399/mo',
    contraindications: [
      'Personal or family history of medullary thyroid carcinoma (MTC)',
      'Multiple endocrine neoplasia syndrome type 2 (MEN2)',
      'Pancreatitis (active or history)',
      'Gastroparesis',
      'Pregnancy or planning pregnancy',
    ],
    warnings: [
      'Nausea is common during titration — slow titration recommended',
      'Risk of gallbladder disease with rapid weight loss',
      'Monitor for pancreatitis symptoms (severe abdominal pain)',
      'Reduce sulfonylurea/insulin doses to avoid hypoglycemia',
      'Adequate protein intake critical to preserve lean mass',
      'Discontinue 2 weeks before elective surgery (gastroparesis risk under anesthesia)',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Labs required. Slow titration protocol provided.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 15,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 16. Men's Sexual Health ---
  {
    id: 'bundle-mens-sexual-health',
    bundleNumber: 16,
    name: "Men's Sexual Health",
    tier: 2,
    target: 'Erectile dysfunction, sexual performance optimization',
    description:
      'PDE5 inhibitor-based sexual health protocol. Daily low-dose tadalafil provides both erectile support and cardiovascular benefits (improved endothelial function), with optional testosterone support.',
    products: [
      { productId: 'tadalafil', productName: 'Tadalafil (Cialis)', dosageOverride: '5mg daily or 10-20mg PRN', isOptional: false, sortOrder: 1 },
      { productId: 'testosterone-cypionate', productName: 'Testosterone Support (if T low)', dosageOverride: 'Per Male HRT protocol if indicated', isOptional: true, sortOrder: 2 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Total Testosterone', operator: 'lt', value: 400 },
      ],
      goalConditions: ['sexual_health'],
      minimumMatchCount: 1,
      description: 'ED symptoms or sexual health optimization goal',
    },
    priceRange: '$179/mo',
    contraindications: [
      'Concurrent nitrate use (nitroglycerin, isosorbide) — CONTRAINDICATED, severe hypotension',
      'Recent MI or stroke (within 6 months)',
      'Severe hypotension (SBP <90)',
      'Unstable angina',
    ],
    warnings: [
      'NEVER combine with nitrates — life-threatening hypotension',
      'Caution with alpha-blockers (additive hypotension)',
      'Priapism risk — seek emergency care if erection >4 hours',
      'May cause back pain, headache, nasal congestion',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Cardiovascular screening required.',
    gender: 'male',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 16,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 17. Women's Sexual Health ---
  {
    id: 'bundle-womens-sexual-health',
    bundleNumber: 17,
    name: "Women's Sexual Health",
    tier: 2,
    target: 'Female sexual dysfunction, low libido',
    description:
      'PT-141 (bremelanotide) for hypoactive sexual desire disorder plus low-dose testosterone cream for libido and energy in women with low androgen levels.',
    products: [
      { productId: 'pt-141', productName: 'PT-141 (Bremelanotide)', dosageOverride: '1.75mg SubQ PRN (max 1 dose/24h, max 8/month)', isOptional: false, sortOrder: 1 },
      { productId: 'testosterone-cream-female', productName: 'Testosterone Cream (Female)', dosageOverride: '0.5-1mg topical daily', isOptional: true, sortOrder: 2 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'DHEA-S', operator: 'lt', value: 150 },
      ],
      goalConditions: ['sexual_health', 'hormonal'],
      minimumMatchCount: 1,
      description: 'HSDD symptoms, low libido, or DHEA-S <150',
    },
    priceRange: '$199/mo',
    contraindications: [
      'Uncontrolled hypertension (PT-141 causes transient BP increase)',
      'Cardiovascular disease',
      'Pregnancy',
    ],
    warnings: [
      'PT-141: nausea is common (40%); darkening of skin/gums possible with repeat use',
      'PT-141: transient blood pressure increase — monitor',
      'Max 8 doses PT-141 per month',
      'Monitor testosterone levels if using cream — avoid virilization',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Labs recommended.',
    gender: 'female',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 17,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 18. Hair Restoration (Male) ---
  {
    id: 'bundle-hair-restoration-male',
    bundleNumber: 18,
    name: 'Hair Restoration (Male)',
    tier: 2,
    target: 'Male pattern hair loss (androgenetic alopecia)',
    description:
      'Dual-mechanism hair loss treatment. Finasteride inhibits 5-alpha reductase (reduces DHT), while oral minoxidil promotes hair growth via vasodilation and potassium channel opening.',
    products: [
      { productId: 'finasteride', productName: 'Finasteride', dosageOverride: '1mg oral daily', isOptional: false, sortOrder: 1 },
      { productId: 'minoxidil-oral', productName: 'Minoxidil (Oral)', dosageOverride: '2.5-5mg oral daily', isOptional: false, sortOrder: 2 },
    ],
    trigger: {
      conditions: [],
      goalConditions: ['body_composition'],
      minimumMatchCount: 1,
      description: 'Androgenetic alopecia (male pattern hair loss)',
    },
    priceRange: '$149/mo',
    contraindications: [
      'Pregnancy or female partner planning pregnancy (finasteride is Category X)',
      'Liver disease (finasteride hepatically metabolized)',
      'Hypotension (oral minoxidil can lower BP)',
    ],
    warnings: [
      'Finasteride: small risk of sexual side effects (2-4%) — usually reversible',
      'Finasteride: do not donate blood while taking (pregnancy risk to recipient)',
      'Oral minoxidil: may cause hypertrichosis (body hair growth), edema, tachycardia',
      'Monitor blood pressure if on oral minoxidil',
      'Results take 3-6 months to become visible',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit.',
    gender: 'male',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 18,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 19. Hair + Skin (Female) ---
  {
    id: 'bundle-hair-skin-female',
    bundleNumber: 19,
    name: 'Hair + Skin (Female)',
    tier: 2,
    target: 'Female pattern hair loss, hormonal acne, skin aging',
    description:
      'Spironolactone for anti-androgen effects (hair loss, acne) combined with tretinoin for skin rejuvenation. Targets hormonal skin and hair concerns in women.',
    products: [
      { productId: 'spironolactone', productName: 'Spironolactone', dosageOverride: '25-100mg oral daily', isOptional: false, sortOrder: 1 },
      { productId: 'tretinoin', productName: 'Tretinoin Cream', dosageOverride: '0.025-0.05% topical nightly', isOptional: false, sortOrder: 2 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'DHEA-S', operator: 'gt', value: 350 },
      ],
      goalConditions: ['body_composition', 'hormonal'],
      minimumMatchCount: 1,
      description: 'Female pattern hair loss, hormonal acne, or elevated androgens',
    },
    priceRange: '$169/mo',
    contraindications: [
      'Pregnancy — BOTH medications are teratogenic (Category X)',
      'Renal impairment (spironolactone is potassium-sparing)',
      'Hyperkalemia',
      'Addison disease',
    ],
    warnings: [
      'PREGNANCY TEST REQUIRED before initiating — both drugs are Category X',
      'Reliable contraception REQUIRED throughout treatment',
      'Spironolactone: monitor potassium at 4 weeks then q6 months',
      'Tretinoin: start every other night, increase to nightly; use sunscreen daily',
      'Tretinoin: skin purging common in first 4-6 weeks',
      'Spironolactone: may cause breast tenderness, irregular periods',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Pregnancy test and potassium monitoring required.',
    gender: 'female',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 19,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 20. Lipid Management ---
  {
    id: 'bundle-lipid-management',
    bundleNumber: 20,
    name: 'Lipid Management',
    tier: 2,
    target: 'LDL >130, apoB >100, elevated cardiovascular risk',
    description:
      'Dual lipid-lowering therapy. Rosuvastatin (HMG-CoA reductase inhibitor) reduces LDL synthesis; ezetimibe blocks intestinal cholesterol absorption for additive LDL reduction of 60-70%.',
    products: [
      { productId: 'rosuvastatin', productName: 'Rosuvastatin', dosageOverride: '5-20mg oral daily', isOptional: false, sortOrder: 1 },
      { productId: 'ezetimibe', productName: 'Ezetimibe', dosageOverride: '10mg oral daily', isOptional: false, sortOrder: 2 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'LDL', operator: 'gt', value: 130 },
        { biomarker: 'LDL Particle Number', operator: 'gt', value: 1200 },
      ],
      goalConditions: ['cardiovascular'],
      minimumMatchCount: 1,
      description: 'LDL >130, LDL-P >1200, or apoB >100',
    },
    priceRange: '$149/mo',
    contraindications: [
      'Active liver disease or unexplained transaminase elevation >3x ULN',
      'Pregnancy (Category X)',
      'Breastfeeding',
    ],
    warnings: [
      'Check liver enzymes (ALT) at baseline and if symptoms arise',
      'Monitor for myalgias — check CK if muscle symptoms develop',
      'Supplement CoQ10 (statins deplete CoQ10)',
      'Check lipid panel at 6-8 weeks after initiation',
      'Grapefruit interaction primarily with atorvastatin/simvastatin, less with rosuvastatin',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Fasting lipid panel required.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 20,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 21. Mental Wellness ---
  {
    id: 'bundle-mental-wellness',
    bundleNumber: 21,
    name: 'Mental Wellness',
    tier: 2,
    target: 'Depression, anxiety, mood optimization',
    description:
      'Evidence-based pharmacotherapy for depression and anxiety. Bupropion (NDRI — activating, weight-neutral) and/or buspirone (5-HT1A partial agonist — non-sedating anxiolytic). Avoids SSRI side effects.',
    products: [
      { productId: 'bupropion', productName: 'Bupropion SR/XL', dosageOverride: '150-300mg daily', isOptional: false, sortOrder: 1 },
      { productId: 'buspirone', productName: 'Buspirone', dosageOverride: '5-15mg 2-3x/day', isOptional: true, sortOrder: 2 },
    ],
    trigger: {
      conditions: [],
      goalConditions: ['energy'],
      minimumMatchCount: 1,
      description: 'Depression, anxiety, or mood optimization (clinical assessment)',
    },
    priceRange: '$149/mo',
    contraindications: [
      'Seizure disorder (bupropion lowers seizure threshold)',
      'Bulimia or anorexia (bupropion contraindicated)',
      'Concurrent MAOI use (wait 14 days)',
      'Abrupt alcohol or benzodiazepine withdrawal (seizure risk with bupropion)',
    ],
    warnings: [
      'Bupropion: may increase anxiety initially — start low',
      'Bupropion: insomnia common — take AM dose only or before 2 PM',
      'Buspirone: takes 2-4 weeks for full anxiolytic effect',
      'Monitor for suicidal ideation (FDA black box warning <25 years)',
      'Do not combine with MAOIs',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Mental health screening required.',
    gender: 'any',
    evidenceLevel: 'strong',
    status: 'active',
    sortOrder: 21,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // ============================================================
  // TIER 3 — Longevity / Peptide Bundles (22-25)
  // ============================================================

  // --- 22. NAD+ Cellular ---
  {
    id: 'bundle-nad-cellular',
    bundleNumber: 22,
    name: 'NAD+ Cellular',
    tier: 3,
    target: 'Aging optimization, cellular energy restoration',
    description:
      'NAD+ repletion protocol combining subcutaneous NAD+ for direct cellular restoration with BPC-157 for tissue repair and systemic regeneration. Entry-level Tier 3 longevity protocol.',
    products: [
      { productId: 'nad-subq', productName: 'NAD+ SubQ', dosageOverride: '100-200mg SubQ 2-3x/week', isOptional: false, sortOrder: 1 },
      { productId: 'bpc-157', productName: 'BPC-157', dosageOverride: '250-500mcg SubQ 2x/day', isOptional: false, sortOrder: 2 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'NAD+ levels', operator: 'lt', value: 'suboptimal' },
      ],
      goalConditions: ['longevity', 'energy'],
      minimumMatchCount: 1,
      description: 'Aging optimization, low NAD+ levels, tissue repair needs',
    },
    priceRange: '$499/mo',
    upsellPath: 'Upgrade to Longevity Core for mTOR inhibition + NAD+ synergy',
    upsellBundleId: 'bundle-longevity-core',
    contraindications: [
      'Active malignancy (BPC-157 promotes angiogenesis — theoretical concern)',
    ],
    warnings: [
      'Injection site rotation required',
      'NAD+ SubQ may cause flushing at higher doses',
      'BPC-157: reconstitute with bacteriostatic water, store refrigerated',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Injection training provided. Ongoing provider oversight required.',
    gender: 'any',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 22,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 23. Longevity Core ---
  {
    id: 'bundle-longevity-core',
    bundleNumber: 23,
    name: 'Longevity Core',
    tier: 3,
    target: 'Advanced longevity protocol — mTOR + AMPK + NAD+ pathways',
    description:
      'The flagship longevity bundle targeting the three master longevity pathways: mTOR inhibition (rapamycin), AMPK activation (metformin), and NAD+ repletion (SubQ NAD+). Based on the most evidence-backed pharmacological longevity interventions.',
    products: [
      { productId: 'rapamycin', productName: 'Rapamycin (Sirolimus)', dosageOverride: '3-6mg weekly (pulsed)', isOptional: false, sortOrder: 1 },
      { productId: 'metformin-er', productName: 'Metformin ER', dosageOverride: '500-1000mg daily', isOptional: false, sortOrder: 2 },
      { productId: 'nad-subq', productName: 'NAD+ SubQ', dosageOverride: '100-200mg SubQ 2-3x/week', isOptional: false, sortOrder: 3 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'Fasting Insulin', operator: 'gt', value: 5 },
        { biomarker: 'hs-CRP', operator: 'gt', value: 0.5 },
      ],
      goalConditions: ['longevity'],
      minimumMatchCount: 1,
      description: 'Advanced longevity optimization — proactive aging intervention',
    },
    priceRange: '$599/mo',
    contraindications: [
      'Active infection (rapamycin is immunomodulatory)',
      'Immunosuppressed patients',
      'eGFR <30 (metformin)',
      'Liver disease (metformin — lactic acidosis)',
      'Pregnancy',
    ],
    warnings: [
      'Rapamycin: monitor lipids, glucose, and CBC q3 months',
      'Rapamycin: hold during active illness',
      'Metformin: GI side effects — titrate slowly',
      'Metformin: annual B12 monitoring',
      'Comprehensive labs required q3 months for Tier 3 oversight',
      'This is an advanced protocol requiring active provider management',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Comprehensive labs required before and q3 months. Active provider oversight mandatory.',
    gender: 'any',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 23,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 24. Cognitive Elite ---
  {
    id: 'bundle-cognitive-elite',
    bundleNumber: 24,
    name: 'Cognitive Elite',
    tier: 3,
    target: 'Cognitive optimization, neuroprotection, brain health',
    description:
      'Advanced nootropic protocol combining methylene blue (mitochondrial electron transport), Semax (BDNF upregulation), and Cerebrolysin (neurotrophic factors). Multi-mechanism cognitive enhancement stack.',
    products: [
      { productId: 'methylene-blue', productName: 'Methylene Blue', dosageOverride: '0.5-1mg/kg oral daily', isOptional: false, sortOrder: 1 },
      { productId: 'semax', productName: 'Semax', dosageOverride: '200-600mcg intranasal daily', isOptional: false, sortOrder: 2 },
      { productId: 'cerebrolysin', productName: 'Cerebrolysin', dosageOverride: '10-30mL IV/IM daily for 10-20 day cycles', isOptional: false, sortOrder: 3 },
    ],
    trigger: {
      conditions: [],
      goalConditions: ['cognition', 'longevity'],
      minimumMatchCount: 1,
      description: 'Cognitive optimization goal, age-related cognitive concerns, or neuroprotection',
    },
    priceRange: '$399/mo',
    contraindications: [
      'G6PD deficiency (methylene blue — hemolytic anemia)',
      'Current SSRI, SNRI, or MAOI use (methylene blue — serotonin syndrome)',
      'Epilepsy or seizure disorder (cerebrolysin)',
      'Porcine allergy (cerebrolysin is porcine-derived)',
      'Pregnancy',
    ],
    warnings: [
      'CRITICAL: G6PD screening required before methylene blue',
      'CRITICAL: Full medication review for serotonergic drugs before methylene blue',
      'Methylene blue: urine/stool will turn blue-green',
      'Cerebrolysin: provider-administered IV/IM only',
      'Semax: available as nasal spray — cycle 3-4 weeks on, 1-2 weeks off',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. G6PD screening and serotonergic drug review mandatory. Cerebrolysin requires in-clinic administration.',
    gender: 'any',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 24,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 25. Immune Reset ---
  {
    id: 'bundle-immune-reset',
    bundleNumber: 25,
    name: 'Immune Reset',
    tier: 3,
    target: 'Chronic immune dysfunction, post-viral syndromes, autoimmune modulation',
    description:
      'Immune restoration protocol combining Thymosin Alpha-1 (T-cell/NK cell activation), low-dose naltrexone (immune modulation via endorphin pathway), and NAC (glutathione precursor, antioxidant). Targets chronic infections, post-COVID, and immune senescence.',
    products: [
      { productId: 'thymosin-alpha-1', productName: 'Thymosin Alpha-1', dosageOverride: '1.6mg SubQ 2x/week', isOptional: false, sortOrder: 1 },
      { productId: 'ldn', productName: 'Low-Dose Naltrexone (LDN)', dosageOverride: '1.5-4.5mg oral at bedtime', isOptional: false, sortOrder: 2 },
      { productId: 'nac', productName: 'N-Acetyl Cysteine (NAC)', dosageOverride: '600-1200mg 2x/day', isOptional: false, sortOrder: 3 },
    ],
    trigger: {
      conditions: [
        { biomarker: 'WBC', operator: 'lt', value: 5.0 },
        { biomarker: 'hs-CRP', operator: 'gt', value: 2.0 },
      ],
      goalConditions: ['immune', 'longevity'],
      minimumMatchCount: 1,
      description: 'Low WBC, chronic inflammation, post-viral syndrome, or autoimmune concerns',
    },
    priceRange: '$449/mo',
    contraindications: [
      'Current opioid use (naltrexone blocks opioid receptors — precipitated withdrawal)',
      'Organ transplant recipients (thymosin may enhance rejection)',
      'Active autoimmune flare (thymosin — use with caution)',
    ],
    warnings: [
      'CRITICAL: Naltrexone is contraindicated with opioid use — screen carefully',
      'LDN: start at 1.5mg, titrate to 4.5mg over 2-4 weeks',
      'LDN: vivid dreams common at initiation',
      'Thymosin Alpha-1: may transiently increase inflammatory markers',
      'NAC: separate from nitroglycerin',
      'Monitor CBC with differential q8 weeks',
    ],
    requiresProvider: true,
    checkoutNote: 'Requires $49 provider visit. Opioid use screening mandatory. Immune panels recommended at baseline and q8 weeks.',
    gender: 'any',
    evidenceLevel: 'moderate',
    status: 'active',
    sortOrder: 25,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },
];
